import React, { useState } from 'react';
import { X, AlertTriangle, Send, CheckCircle2, ShieldAlert } from 'lucide-react';
import { RoteiroOperacionalAtivo } from '../../types/operacional';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  roteiro: RoteiroOperacionalAtivo | null;
  onSalvarOcorrencia: (roteiroId: number, tipo: string, descricao: string) => Promise<boolean>;
}

const TIPOS_OCORRENCIA = [
  { id: 'atraso_transito', label: '🚦 Atraso no Trânsito / Congestionamento' },
  { id: 'cliente_ausente', label: '🏢 Cliente Ausente / Fechado' },
  { id: 'endereco_incorreto', label: '📍 Endereço Não Localizado / Incorreto' },
  { id: 'problema_veiculo', label: '🔧 Pane Mecânica / Pneu Furado' },
  { id: 'recusa_carga', label: '❌ Recusa de Recebimento' },
  { id: 'avaria', label: '⚠️ Avaria Identificada na Carga' },
  { id: 'outro', label: '📝 Outra Ocorrência Operacional' },
];

export const OcorrenciaOperacionalModal: React.FC<Props> = ({
  isOpen,
  onClose,
  roteiro,
  onSalvarOcorrencia
}) => {
  const [tipo, setTipo] = useState('atraso_transito');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!isOpen || !roteiro) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!descricao.trim()) return;

    setSalvando(true);
    setErro(null);
    const ok = await onSalvarOcorrencia(roteiro.id, tipo, descricao);
    setSalvando(false);

    if (!ok) {
      setErro('Não foi possível registrar a ocorrência. Tente novamente.');
      return;
    }

    setSucesso(true);
    setTimeout(() => {
      setSucesso(false);
      setDescricao('');
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-200">
        <div className="p-4 bg-red-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-300" />
            <h3 className="font-bold text-base">Registrar Ocorrência Operacional</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs text-slate-700">
            <p><strong>Motorista:</strong> {roteiro.motorista_nome}</p>
            <p><strong>Veículo:</strong> {roteiro.veiculo_placa} - {roteiro.veiculo_modelo}</p>
            <p><strong>Roteiro:</strong> {roteiro.nome_roteiro}</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
              Tipo da Ocorrência
            </label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full text-sm border-slate-300 rounded-lg focus:ring-red-500 focus:border-red-500 p-2.5 border"
            >
              {TIPOS_OCORRENCIA.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
              Descrição do Ocorrido / Ação Tomada
            </label>
            <textarea
              required
              rows={4}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva detalhes do imprevisto e orientações passadas ao motorista..."
              className="w-full text-sm border-slate-300 rounded-lg focus:ring-red-500 focus:border-red-500 p-2.5 border"
            />
          </div>

          {erro && (
            <div className="bg-rose-50 text-rose-700 p-3 rounded-xl border border-rose-200 text-sm flex items-center gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
              {erro}
            </div>
          )}

          {sucesso ? (
            <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl border border-emerald-200 text-sm flex items-center justify-center gap-2 font-semibold">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Ocorrência registrada com sucesso!
            </div>
          ) : (
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 font-semibold hover:bg-slate-50 transition"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={salvando || !descricao.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition shadow"
              >
                <Send className="h-4 w-4" />
                {salvando ? 'Registrando...' : 'Salvar Ocorrência'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
